// JOBE-0162.4: OrganizationEmpty — branching empty-state copy.
//
// Each branch carries a distinct `data-testid` so callers (and tests)
// can grep the specific reason. The copy is intentionally short and
// shape-aware: a search miss echoes the query; a dept+type miss names
// both; an owner-filter miss explains the cause.
//
// Per D14 ("nothing is decorative"), the empty-state surface always
// renders alongside the filter chrome — the user must have a way to
// recover from the empty result.

import React from 'react';

function titleCase(slug) {
  if (!slug) return '';
  return slug
    .split(/[-_\s]+/)
    .map((p) => (p.length === 0 ? '' : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(' ');
}

/**
 * @param {object} props
 * @param {'all'|'human'|'agent'} [props.typeFilter='all']
 * @param {string} [props.deptFilter='all']
 * @param {string} [props.query='']
 * @param {string|null} [props.ownerId=null]
 * @param {number} [props.totalRows=0]   total rows pre-filter (drives the
 *                                       "no-data" vs "filtered-to-nothing"
 *                                       distinction)
 */
export function OrganizationEmpty({
  typeFilter = 'all',
  deptFilter = 'all',
  query = '',
  ownerId = null,
  totalRows = 0,
}) {
  if (query) {
    return (
      <div className="org-empty" data-testid="org-empty-noresults">
        No results for &ldquo;<b>{query}</b>&rdquo;.
      </div>
    );
  }
  if (deptFilter !== 'all' && typeFilter === 'agent') {
    return (
      <div className="org-empty" data-testid="org-empty-nodept">
        No agents in <b>{titleCase(deptFilter)}</b>.
      </div>
    );
  }
  if (deptFilter !== 'all' && typeFilter === 'human') {
    return (
      <div className="org-empty" data-testid="org-empty-nodept">
        No humans in <b>{titleCase(deptFilter)}</b>.
      </div>
    );
  }
  if (deptFilter !== 'all') {
    return (
      <div className="org-empty" data-testid="org-empty-nodept">
        No people or agents in <b>{titleCase(deptFilter)}</b>.
      </div>
    );
  }
  if (ownerId) {
    return (
      <div className="org-empty" data-testid="org-empty-noowned">
        No rows owned by this person.
      </div>
    );
  }
  if (totalRows === 0) {
    return (
      <div className="org-empty" data-testid="org-empty-nodata">
        No people or agents in the directory.
      </div>
    );
  }
  return (
    <div className="org-empty" data-testid="org-empty-generic">
      No matches for the current filter.
    </div>
  );
}
